import React from "react";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, FlaskConical, Search, CheckCircle2 } from "lucide-react";

// ---------------------------------------------------------------------------
// MP12 Module 3 §E — Asset Pathway status vocabulary.
//
// The badge component below renders exactly one of the four canonical
// statuses. Colours are chosen from the shared palette so the badge reads
// clearly against both light and dark chrome.
// ---------------------------------------------------------------------------

export const ASSET_PATHWAY_STATUSES = [
  "DEMO_ONLY",
  "DEVELOPMENT_CANDIDATE",
  "CLEARANCE_REVIEW",
  "CLIENT_READY_RELEASE_ELIGIBLE",
];

export const ASSET_PATHWAY_LABELS = {
  DEMO_ONLY:                     "DEMO ONLY",
  DEVELOPMENT_CANDIDATE:         "DEVELOPMENT CANDIDATE",
  CLEARANCE_REVIEW:              "CLEARANCE REVIEW",
  CLIENT_READY_RELEASE_ELIGIBLE: "CLIENT READY / RELEASE ELIGIBLE",
};

export const ASSET_PATHWAY_DESCRIPTIONS = {
  DEMO_ONLY: (
    "Demonstration / reference material only. Blocked from licensing, sync submission, release-ready status, public catalogue visibility, and clearance completion."
  ),
  DEVELOPMENT_CANDIDATE: (
    "May be reviewed and developed internally. Not release-ready, client-ready, publicly catalogue-eligible, or sync-submission eligible until an authorised human elevates it."
  ),
  CLEARANCE_REVIEW: (
    "Inside the compliance and clearance workflow. Not publicly catalogued, release-ready, or submitted for sync until the readiness gates are satisfied."
  ),
  CLIENT_READY_RELEASE_ELIGIBLE: (
    "May enter approved client, release, catalogue, clearance, and sync workflows once relevant readiness gates are satisfied. The pathway label alone is not a legal clearance determination."
  ),
};

const STYLE = {
  DEMO_ONLY:                     { bg: "hsl(0 55% 94%)",   fg: "hsl(0 65% 30%)",   border: "hsl(0 55% 72%)",   Icon: FlaskConical },
  DEVELOPMENT_CANDIDATE:         { bg: "hsl(40 55% 94%)",  fg: "hsl(35 60% 28%)",  border: "hsl(40 55% 76%)",  Icon: ShieldAlert },
  CLEARANCE_REVIEW:              { bg: "hsl(210 45% 94%)", fg: "hsl(210 55% 30%)", border: "hsl(210 45% 74%)", Icon: Search },
  CLIENT_READY_RELEASE_ELIGIBLE: { bg: "hsl(150 45% 92%)", fg: "hsl(150 45% 24%)", border: "hsl(150 40% 66%)", Icon: CheckCircle2 },
};

export function AssetPathwayBadge({ value, className = "", showIcon = true, testid }) {
  const code = (value || "").trim();
  const label = ASSET_PATHWAY_LABELS[code] || code || "—";
  const st = STYLE[code] || { bg: "hsl(220 8% 94%)", fg: "hsl(220 6% 40%)", border: "hsl(220 6% 78%)", Icon: ShieldAlert };
  const Icon = st.Icon;
  return (
    <Badge
      variant="outline"
      className={`text-[10px] uppercase tracking-[0.14em] font-semibold inline-flex items-center gap-1 ${className}`}
      style={{ background: st.bg, color: st.fg, borderColor: st.border }}
      data-testid={testid || "asset-pathway-badge"}
      title={ASSET_PATHWAY_DESCRIPTIONS[code] || ""}
    >
      {showIcon && <Icon className="w-3 h-3" />}
      {label}
    </Badge>
  );
}

export default AssetPathwayBadge;
